import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="border-b border-[#0A0A0A]/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors w-fit">
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-2">Privacy Policy</h1>
        <p className="text-[#525252] mb-8">Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">1. Introduction</h2>
            <p className="text-[#525252] leading-relaxed">
              Sol Digital ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. This policy is in compliance with the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 of India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">2.1 Personal Information</h3>
            <p className="text-[#525252] leading-relaxed mb-4">We may collect the following personal information:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Name and contact details (email address, phone number)</li>
              <li>Billing and shipping address</li>
              <li>Company name and job title</li>
              <li>Profile photos and company logos you upload</li>
              <li>Social media profile links</li>
              <li>Payment information (processed securely through Razorpay)</li>
            </ul>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>IP address and browser type</li>
              <li>Device information</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">3. How We Use Your Information</h2>
            <p className="text-[#525252] leading-relaxed mb-4">We use the collected information for:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Creating and managing your digital business card profile</li>
              <li>Processing orders and delivering NFC cards</li>
              <li>Processing payments through Razorpay</li>
              <li>Sending order confirmations and shipping updates</li>
              <li>Providing customer support</li>
              <li>Improving our services and user experience</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-[#525252] leading-relaxed mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li><strong>Payment Processors:</strong> Razorpay for secure payment processing</li>
              <li><strong>Shipping Partners:</strong> For delivering physical NFC cards</li>
              <li><strong>Service Providers:</strong> Third-party services that help us operate our business</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-[#525252] leading-relaxed mt-4">
              We do not sell, trade, or rent your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">5. Data Security</h2>
            <p className="text-[#525252] leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">6. Data Retention</h2>
            <p className="text-[#525252] leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">7. Your Rights</h2>
            <p className="text-[#525252] leading-relaxed mb-4">Under applicable Indian law, you have the right to:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your personal information</li>
              <li>Withdraw consent for data processing</li>
              <li>Object to certain data processing activities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">8. Cookies Policy</h2>
            <p className="text-[#525252] leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie preferences through your browser settings. Essential cookies are required for the website to function properly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">9. Third-Party Links</h2>
            <p className="text-[#525252] leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">10. Children's Privacy</h2>
            <p className="text-[#525252] leading-relaxed">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child, we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">11. Changes to This Policy</h2>
            <p className="text-[#525252] leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">12. Contact Us</h2>
            <p className="text-[#525252] leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mt-4">
              <p className="text-[#0A0A0A] font-medium">Sol Digital</p>
              <p className="text-[#525252]">Email: support@soldigital.in</p>
              <p className="text-[#525252]">Address: India</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">13. Grievance Officer</h2>
            <p className="text-[#525252] leading-relaxed">
              In accordance with Information Technology Act 2000 and rules made there under, the name and contact details of the Grievance Officer are provided below:
            </p>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mt-4">
              <p className="text-[#0A0A0A] font-medium">Grievance Officer</p>
              <p className="text-[#525252]">Email: grievance@soldigital.in</p>
              <p className="text-[#525252]">Response Time: Within 30 days of receiving the complaint</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
